/**
 * NodeSearchBar — autocomplete picker over the Shura graph nodes.
 *
 * Used in two places on the Shura Nexus page:
 *   • The header "Find a node…" bar (selects + zooms the graph).
 *   • Inside <LiveConnectPanel/> — two of these stacked.
 *
 * Generic enough to be a controlled input: parent owns the selected
 * node id and what to do with it.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';

const KIND_DOT = {
  startup:      '#7C3AED',
  investor:     '#D4AF37',
  holding:      '#3B82F6',
  subsidiary:   '#60A5FA',
  person:       '#06B6D4',
  government:   '#FFFFFF',
  university:   '#10B981',
  rd_center:    '#22D3EE',
  service_provider: '#94A3B8',
};

export default function NodeSearchBar({
  nodes,
  value = null,
  onSelect,
  placeholder = 'Find a node…',
  isDark = false,
  testId = 'node-search',
  size = 'md',           // 'sm' for compact pickers, 'md' for header
  allowedKinds = null,   // optional filter; null = all
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(-1);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selectedNode = useMemo(
    () => (value ? nodes.find((n) => n.id === value) : null),
    [nodes, value],
  );

  const results = useMemo(() => {
    const query = q.trim().toLowerCase();
    const pool = allowedKinds ? nodes.filter((n) => allowedKinds.includes(n.kind)) : nodes;
    if (!query) return pool.slice(0, 12);
    return pool
      .filter((n) => (n.label || '').toLowerCase().includes(query))
      .slice(0, 12);
  }, [nodes, q, allowedKinds]);

  const isSm = size === 'sm';
  const padding = isSm ? 'h-8 pl-7 pr-7 text-xs' : 'h-10 pl-9 pr-9 text-sm';
  const iconLeft = isSm ? 'left-2 top-2' : 'left-2.5 top-2.5';
  const iconRight = isSm ? 'right-2 top-2' : 'right-2.5 top-2.5';
  const iconSize = isSm ? 13 : 15;

  const pickNode = (n) => {
    setQ('');
    setOpen(false);
    onSelect?.(n);
  };

  return (
    <div ref={wrapRef} className="relative w-full" data-testid={`${testId}-wrap`}>
      <div className="relative">
        <Search size={iconSize} className={`absolute ${iconLeft} text-slate-400 pointer-events-none`} />
        <input
          type="text"
          data-testid={testId}
          value={selectedNode && !open ? selectedNode.label : q}
          onFocus={() => { setOpen(true); if (selectedNode) setQ(selectedNode.label); }}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setFocus((f) => Math.min(f + 1, results.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setFocus((f) => Math.max(f - 1, 0)); }
            else if (e.key === 'Enter' && focus >= 0 && results[focus]) { e.preventDefault(); pickNode(results[focus]); }
            else if (e.key === 'Escape') { setOpen(false); }
          }}
          placeholder={placeholder}
          className={`w-full ${padding} rounded-md border focus:outline-none focus:ring-2 focus:ring-[#8B3FC5]/30 focus:border-[#8B3FC5] transition
            ${isDark
              ? 'bg-white/[0.04] border-white/10 text-white placeholder:text-white/40'
              : 'bg-white border-slate-200 text-slate-900 placeholder:text-slate-400'}`}
        />
        {(selectedNode || q) && (
          <button
            type="button"
            data-testid={`${testId}-clear`}
            onMouseDown={(e) => { e.preventDefault(); setQ(''); onSelect?.(null); setOpen(false); }}
            className={`absolute ${iconRight} text-slate-400 hover:text-slate-700 dark:hover:text-white`}
            aria-label="Clear selection"
          >
            <X size={iconSize}/>
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <ul
          role="listbox"
          data-testid={`${testId}-results`}
          className={`absolute z-30 mt-1 w-full max-h-72 overflow-y-auto rounded-md shadow-xl border
            ${isDark
              ? 'bg-[#0d0d0d] border-white/10'
              : 'bg-white border-slate-200'}`}
        >
          {results.map((n, i) => (
            <li key={n.id}>
              <button
                type="button"
                data-testid={`${testId}-option-${i}`}
                onMouseEnter={() => setFocus(i)}
                onMouseDown={(e) => { e.preventDefault(); pickNode(n); }}
                className={`w-full text-left px-3 py-2 flex items-center gap-2 transition
                  ${focus === i
                    ? (isDark ? 'bg-white/[0.06]' : 'bg-[#FFF8E5]')
                    : (isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-slate-50')}`}
              >
                <span
                  className="inline-block h-2 w-2 rounded-full shrink-0"
                  style={{ backgroundColor: KIND_DOT[n.kind] || '#94A3B8' }}
                />
                <span className={`flex-1 min-w-0 truncate text-xs ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  {n.label}
                </span>
                <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-slate-400">
                  {n.kind}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
